// Ordering for human-facing text — task names, resource names, group names.
//
// Sugar's `Array.sortBy` did not compare strings with `<` / `>`. It ran them
// through a bespoke collation: case-insensitive, accent-sensitive, with runs of
// digits compared numerically ("Task 2" before "Task 10"). Roughly 68 call sites
// depended on that, so replacing Sugar with a naive comparator would silently
// reorder tasks, resources and groups everywhere they are listed.
//
// `Intl.Collator` reproduces it with the standard algorithm instead of a
// hand-maintained table. See `text-collation.spec.ts`, which pins the ordering
// rules this module is relied on for.
//
// Deliberate differences from Sugar, accepted when this replaced it:
//
//   * Sugar's base alphabet placed Å, Ä and Ö *after* Z — the Scandinavian
//     convention. Under `en-AU` they sort next to A and O, which is what our
//     users expect. Nordic names are the main place existing plans reorder.
//   * Sugar parsed a leading digit run even across a decimal point, so
//     "3.1 Survey" sorted after "3M Australia". Intl treats the letter run as
//     text, giving "3.1 Survey" first.
//   * Punctuation is weighted by the standard collation rather than by raw
//     character code, so "Design (draft)" and "Design - rev A" can swap.
//
// Everything else — plain Latin names, digits, and CJK — matched Sugar exactly
// when measured pair-by-pair over realistic plan data.

/**
 * The locale is pinned on purpose. `Intl.Collator` with an undefined locale
 * reads the host machine, so the same shared plan would order differently for
 * two people looking at it. Sugar's ordering was machine-independent and that
 * property has to survive the migration.
 */
const COLLATION_LOCALE = 'en-AU';

const collator = new Intl.Collator(COLLATION_LOCALE, {
    // "Task 2" before "Task 10" rather than lexicographically.
    numeric: true,
    // Case is ignored ("alice" == "Alice"), but accents still distinguish
    // ("Ana" < "Ána"), matching how Sugar lowercased before collating and then
    // broke exact ties on the accented form.
    sensitivity: 'accent',
});

/**
 * Compare two strings the way user-visible lists are ordered, returning the
 * usual negative / zero / positive.
 *
 * Takes `string` rather than `string | null | undefined` on purpose: Sugar
 * compared a nullish key against anything as *equal*, which quietly left those
 * rows wherever they happened to sit. Requiring a string forces each call site
 * to say what it wants — usually `item.name ?? ''`.
 */
export function compareText(a: string, b: string): number {
    return collator.compare(a, b);
}

/**
 * A `compare` option for {@link arraySortBy} when the selected key is
 * human-facing text.
 *
 * Only for text. Keys that are numbers, dates, or ordinal strings — notably the
 * lexicographically-sortable fractional-index `order` fields — must keep the
 * default ordinal comparison, which this would corrupt by ignoring case and
 * reading digit runs as numbers.
 */
export const textCompareOption = { compare: compareText } as const;

/**
 * Compare two sort keys the way `Sugar.Array.sortBy` did: strings go through the
 * collation above, anything else compares with `<` / `>`.
 *
 * Sugar dispatched on the runtime type of the selected key, so a single call
 * site could sort names one moment and timestamps the next and get the right
 * answer for both. Reproducing that dispatch is what makes replacing those call
 * sites a no-op, instead of 60-odd individual judgements about whether a given
 * selector happens to return text.
 */
export function compareValues<K>(a: K, b: K): number {
    if (typeof a === 'string' && typeof b === 'string') {
        return collator.compare(a, b);
    }
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}

/**
 * The `compare` option matching `Sugar.Array.sortBy`'s ordering, for the call
 * sites migrated off it. Prefer {@link textCompareOption} in new code where the
 * key is known to be text, and the default ordinal comparison where it is not.
 */
export const sortKeyCompareOption: { compare: (a: any, b: any) => number } = {
    compare: compareValues,
};
